import {t, TFunction} from 'i18next';
import {GridSortModel} from '@mui/x-data-grid';
import _ from 'lodash';
import {ReviewResultsSources} from '../../../../../shared/interfaces';
import {
  AccordionSection,
  ColumnConfig,
  FilterComponent,
  Reports,
  Section,
} from '../../config/reportTypes';
import {buildTreeItems} from '../../../../../utils/reportUtils';
import {
  ABMAssignmentResultItemType,
  ABMDocResultItemType,
  GetABMDocJSONResultReqPayload,
  GetABMJSONResultReqPayload,
  LeftPanelReportResponse,
  SaveABMReportDataReqPayload,
} from '../../../../../api/types/reportingApi.type';
import {
  ReportActionMenuOptionTypes,
  SourceTypeCode,
} from '../../../../../shared/constants';
import {CustomMenuItem} from '../../../../common/arr-menu/ArrMenu';
import {getSectionMeta} from '../../component/left-panel/components/ArrSourceSelectedSection';

const baseWidthOfDataGrid = 1048;
const docStringPath = 'reports.advanceBatchManagement.documentGrid.';
const assignmentStringPath = 'reports.advanceBatchManagement.assignmentGrid.';

const documentSortingKeyMap: {[Key: string]: string} = {
  documentID: 'DOCID',
  fullPath: 'FullPath',
  docFileType: 'Docfiletype',
  assignment: 'Assignment',
  assignmentPath: 'AssignmentPath',
  reviewerBatch: 'ReviewerBatch',
  status: 'Status',
};
const assignmentSortingKeyMap: {[Key: string]: string} = {
  assignment: 'Assignment',
  assignmentPath: 'Assignmentpath',
  reviewerBatch: 'ReviewerBatch',
  InSet: 'InSet',
  InSetCompleted: 'InSetCompleted',
  InSetLeftToDo: 'InSetLeftToDo',
};

type TreeItem = {
  id: string;
  fileType: string;
  children: TreeItem[];
};

export const sourcesSectionKeys = ['SEARCHES', 'ASSIGNMENTS', 'FOLDERS'];
export type BatchLevel = 'Document' | 'Assignment';
export const updateApplyButtonState = (
  report: Reports,
  filterValues: Record<string, any>
) => {
  const isSourceSelected: boolean = sourcesSectionKeys.some((key) => {
    const value = _.get(filterValues, key, []);
    return Array.isArray(value) && value.length > 0;
  });

  const isReviewerSelected: boolean =
    Array.isArray(filterValues.selectedReviewers) &&
    filterValues.selectedReviewers.some((v) => v === true);

  const isAssignmentsSelected: boolean =
    Array.isArray(filterValues.selectedAssignments) &&
    filterValues.selectedAssignments.length > 0;

  const appplyButtonDisabled =
    !isSourceSelected || !isReviewerSelected || !isAssignmentsSelected;

  const config = report.config;
  const configObject = {
    ...config,
    selectionCriteria: {
      ...config.selectionCriteria,
      actions: config.selectionCriteria.actions.map((action) => {
        if (
          action.input_type === 'button' &&
          action.actionKey === 'applyChanges'
        ) {
          return {
            ...action,
            disabled: appplyButtonDisabled,
          };
        } else if (
          action.input_type === 'button' &&
          action.actionKey === 'reset'
        ) {
          return {
            ...action,
            disabled:
              !isReviewerSelected &&
              !isAssignmentsSelected &&
              !isSourceSelected,
          };
        } else {
          return action;
        }
      }),
    },
  };
  return {
    ...report,
    config: configObject,
  };
};

const getFolderIds = (arr: TreeItem[]): string[] => {
  let folderIds: string[] = [];

  arr.forEach((item) => {
    if (item.fileType === 'folder') {
      folderIds.push(item.id);
      if (item.children && item.children.length > 0) {
        folderIds = folderIds.concat(getFolderIds(item.children));
      }
    }
  });

  return folderIds;
};

export const mergeApiDataIntoConfig = (
  report: Reports,
  apiData: ReviewResultsSources,
  savedReportData?: LeftPanelReportResponse
) => {
  const config = report.config;
  const configObject = {
    sections: config.sections.map((section: Section) => ({
      ...section,
      filters: section.filters.map((filter: AccordionSection) => {
        return {
          ...filter,
          components: filter.components.map((comp: FilterComponent) => {
            if (
              comp.input_type === 'checkboxList' &&
              filter.id === 'reviewersList'
            ) {
              const transformedData = apiData.reviewersList.map((reviewer) => ({
                label: reviewer.kayName,
                value: (savedReportData?.selectedReviewers ?? []).includes(
                  reviewer.keyID
                ),
              }));
              return {
                ...comp,
                childLabels: transformedData,
                initialValues: transformedData.map((item) => item.value),
              };
            } else if (
              comp.input_type === 'multiSelectTreeViewList' &&
              filter.id === 'assignments'
            ) {
              const transformedData = buildTreeItems(apiData.assignmentsList);
              return {
                ...comp,
                treeData: transformedData,
                defaultExpandedIds: getFolderIds(transformedData),
                initialValues: (savedReportData?.selectedAssignments ?? []).map(
                  (id) => id.toString()
                ),
              };
            }
            return comp;
          }),
        };
      }),
    })),
  };
  return {
    ...report,
    config: {
      selectionCriteria: config.selectionCriteria,
      sections: configObject.sections,
    },
  };
};

export const getABMJSONResultReqPayload = (
  batchLevel: BatchLevel,
  reportID: number,
  pageNuber: number,
  pageSize: number,
  sortingcolumnName: string,
  sortingDirection: string
): GetABMJSONResultReqPayload | GetABMDocJSONResultReqPayload => {
  if (batchLevel === 'Document') {
    return {
      reportID: reportID,
      pageNumber: pageNuber,
      pageSize: pageSize,
      sortingcolumnName: sortingcolumnName,
      sortingDirection: sortingDirection,
    };
  }
  return {
    reportID: reportID,
    pageNumber: pageNuber,
    pageSize: pageSize,
    sortingcolumnName: sortingcolumnName,
    sortingDirection: sortingDirection,
  };
};
export const getSourceDetailForAction = (
  reportPayload: SaveABMReportDataReqPayload,
  resultData: (ABMDocResultItemType | ABMAssignmentResultItemType)[],
  selectedAssignmentIds?: Map<string, string[]>,
  selectedDocIds: Map<string, ABMDocResultItemType> = new Map(),
  isAllDocSelected: boolean = false
) => {
  const payload: {[key: string]: any} = {
    IsBulkAction: 1,
    NavigationSourceContext: SourceTypeCode.ADVBATMGMTRPT,
    NavigationDestinationContext: '',
    BatchLevel: reportPayload.batchLevel,
    SelectedSourceID: reportPayload.selectedSourceID.map((id) => Number(id)),
    SelectedReviewers: reportPayload.selectedReviewers.map((id) => Number(id)),
    SelectedAssignments: reportPayload.selectedAssignments.map((id) =>
      Number(id)
    ),
    ReportTypeCode: 'ABM001',
    TypeOfReport: reportPayload.typeOfReport,
    SelectedSource: reportPayload.selectedSource,
    SortingColumn: reportPayload.sortingColumn,
    IsAscending: reportPayload.isAscending,
    PageNumber: reportPayload.pageNumber,
    PageSize: reportPayload.pageSize,
    RowNumber: reportPayload.rowNumber,
    SourceTypeCode: SourceTypeCode.ADVBATMGMTRPT,
  };

  if (reportPayload.batchLevel === 'ASSIGNMENT' && selectedAssignmentIds) {
    const result = [];

    for (const [assignmentId, values] of selectedAssignmentIds.entries()) {
      const assignment = resultData.find(
        (data) => (data as ABMAssignmentResultItemType).id === assignmentId
      ) as ABMAssignmentResultItemType | undefined;
      if (assignment === undefined) continue;

      const obj: {[key: string]: any} = {
        AssignmentID: Number(assignment.assignmentID),
        Reviewer: assignment.reviewer,
      };
      if (values.includes('InSet')) obj['InSet'] = 1;
      if (values.includes('InSetCompleted')) obj['InSetCompleted'] = 1;
      if (values.includes('InSetLeftToDo')) obj['InSetLeftToDo'] = 1;
      result.push(obj);
    }
    payload.BulkActionABMAssignment = result;
  } else if (reportPayload.batchLevel === 'DOCUMENT') {
    payload.FilterDocumentGrid = reportPayload.filterDocumentGrid;
    const actionDocs: {
      DocumentID: number;
      AssignmentID: number;
      ReviewerID: string;
      status: string;
    }[] = [];
    if (!isAllDocSelected) {
      selectedDocIds.forEach((doc) => {
        actionDocs.push({
          DocumentID: doc.documentID,
          AssignmentID: doc.assignmentID,
          ReviewerID: doc.reviewerID,
          status: doc.status.replace(' ', ' '),
        });
      });
    }
    payload.BulkActionABMDocument = actionDocs;
    payload.IsSelectedAll = isAllDocSelected;
  }
  return payload;
};

export const getRedistributeActionData = (
  batchLevel: BatchLevel,
  resultData: (ABMDocResultItemType | ABMAssignmentResultItemType)[],
  selectedAssignments: Map<string, string[]>,
  selectedDocIds: Map<string, ABMDocResultItemType>
): {
  redistributeCount: number;
  assignmentId: number;
  currentReviewerId: string;
} => {
  let redistributeCount = 0;
  let assignmentId = 0;
  let currentReviewerId = '';

  if (batchLevel === 'Assignment') {
    const firstKey = selectedAssignments.keys().next().value;
    const assignmentData = resultData.find(
      (data) => (data as ABMAssignmentResultItemType).id === firstKey
    ) as ABMAssignmentResultItemType | undefined;
    if (assignmentData) {
      assignmentId = assignmentData.assignmentID;
      currentReviewerId =
        (assignmentData.reviewer ?? '0') !== '' ? assignmentData.reviewer : '0';
      redistributeCount = assignmentData.inSetLeftToDo;
    }
  } else if (batchLevel === 'Document') {
    redistributeCount = selectedDocIds.size;
    const doc = selectedDocIds.values().next().value;
    if (doc) {
      assignmentId = doc.assignmentID;
      currentReviewerId = (doc.reviewerID ?? '') !== '' ? doc.reviewerID : '0';
    }
  }
  return {
    redistributeCount,
    assignmentId,
    currentReviewerId,
  };
};

export const getBulkActionExecutePayload = (data: {
  operationType?: string;
  sourceDetails?: {[key: string]: any};
}): {
  selectedList: {[key: string]: any};
  destinationList: {[key: string]: any};
} => {
  const {sourceDetails = {}} = data;

  const selectedList: {[key: string]: any} = {
    ...sourceDetails,
    SelectedSourceID: sourceDetails?.SelectedSourceID.map((id: number) =>
      id.toString()
    ),
    SelectedReviewers: sourceDetails?.SelectedReviewers.map((id: number) =>
      id.toString()
    ),
    SelectedAssignments: sourceDetails?.SelectedAssignments.map((id: number) =>
      id.toString()
    ),
  };

  if (sourceDetails?.BatchLevel === 'ASSIGNMENT') {
    selectedList.BulkActionABMAssignment =
      sourceDetails?.BulkActionABMAssignment.map((entry: any) => ({
        ...entry,
        AssignmentID: entry.AssignmentID.toString(),
      }));
  } else if (sourceDetails?.BatchLevel === 'DOCUMENT') {
    selectedList.FilterDocumentGrid = sourceDetails?.FilterDocumentGrid;
    selectedList.FilterDocumentGrid = sourceDetails?.FilterDocumentGrid;
    selectedList.IsSelectedAll = sourceDetails?.IsSelectedAll;
    selectedList.BulkActionABMDocument =
      sourceDetails?.BulkActionABMDocument.map((entry: any) => ({
        ...entry,
        DocumentID: entry.DocumentID.toString(),
        AssignmentID: entry.AssignmentID.toString(),
        ReviewerID: entry.ReviewerID.toString(),
      }));
  }
  return {selectedList, destinationList: {}};
};

export const getSaveABMReportDataPayload = (
  reportConfig: Reports,
  filterValues: Record<string, any>,
  batchLevel: BatchLevel,
  apiData?: ReviewResultsSources,
  filterDocumentGrid?: string,
  sortingColumn?: string
) => {
  const payload: SaveABMReportDataReqPayload = {
    batchLevel: batchLevel.toUpperCase(),
    reportTypeCode: 'ABM001', // Need to check with BE
    typeOfReport: batchLevel === 'Document' ? '2' : '1',
    sortingColumn: sortingColumn ?? 'Assignment',
    isAscending: 'asc',
    pageNumber: -1,
    pageSize: -1,
    rowNumber: 0,
    selectedReviewers: [],
    selectedAssignments: [],
    selectedSourceID: [],
    selectedSource: '',
  };

  if (filterDocumentGrid) {
    payload.filterDocumentGrid = filterDocumentGrid; // apply for only BatchLevel = Document
  }

  // Iterate through the report config to map filter values to payload based on section filter component
  reportConfig.config.sections.forEach((section: Section) => {
    section.filters.forEach((filter: AccordionSection) => {
      filter.components.forEach((comp: FilterComponent) => {
        const key = comp.key;
        if (key === 'selectedAssignments') {
          payload.selectedAssignments = _.get(filterValues, key, []);
        } else if (key === 'selectedReviewers') {
          const selectedReviewers: string[] = [];
          _.get(filterValues, key, []).forEach(
            (isSelected: boolean, index: number) => {
              if (isSelected && apiData) {
                selectedReviewers.push(
                  _.get(apiData.reviewersList[index], 'keyID', '').toString()
                );
              }
            }
          );
          payload.selectedReviewers = selectedReviewers;
        }
      });
    });
  });

  // Set sources in payload
  sourcesSectionKeys.forEach((sourceKey) => {
    if (_.get(filterValues, sourceKey, []).length > 0) {
      payload.selectedSourceID = filterValues[sourceKey][0].source;
      payload.selectedSource = filterValues[sourceKey][0].sourceName;
    }
  });
  return payload;
};

export const getColumnSortingKey = (
  sortModal: GridSortModel,
  batchLevel: BatchLevel
) => {
  switch (batchLevel) {
    case 'Document':
      if (sortModal.length > 0) {
        return (
          documentSortingKeyMap[sortModal[0].field] ??
          documentSortingKeyMap.documentID
        );
      }
      return documentSortingKeyMap.documentID;
    case 'Assignment':
      if (sortModal.length > 0) {
        return (
          assignmentSortingKeyMap[sortModal[0].field] ??
          assignmentSortingKeyMap.assignment
        );
      }
      return assignmentSortingKeyMap.assignment; // Default sorting by assignment;
    default:
      return assignmentSortingKeyMap.assignment; // Default sorting by assignment;
  }
};

export const getDefaultSortingForBatchLevel = (
  batchLevel: BatchLevel
): GridSortModel => {
  if (batchLevel === 'Document') {
    return [
      {
        field: 'documentID',
        sort: 'asc',
      },
    ];
  }
  return [
    {
      field: 'assignment',
      sort: 'asc',
    },
  ];
};

export const documentColumns: ColumnConfig[] = [
  {
    field: 'documentID',
    headerName: t(docStringPath + 'docId'),
    flex: 99 / baseWidthOfDataGrid,
    minWidth: 99,
    isVisible: true,
  },
  {
    field: 'fullPath',
    headerName: t(docStringPath + 'fullPath'),
    flex: 296 / baseWidthOfDataGrid,
    minWidth: 296,
    isVisible: true,
  },
  {
    field: 'docFileType',
    headerName: t(docStringPath + 'docFileType'),
    flex: 112 / baseWidthOfDataGrid,
    minWidth: 112,
    isVisible: true,
  },
  {
    field: 'assignment',
    headerName: t(docStringPath + 'assignment'),
    flex: 130 / baseWidthOfDataGrid,
    minWidth: 130,
    isVisible: true,
  },
  {
    headerName: t(docStringPath + 'assignmentPath'),
    field: 'assignmentPath',
    flex: 122 / baseWidthOfDataGrid,
    minWidth: 122,
    isVisible: true,
  },
  {
    headerName: t(docStringPath + 'reviewerBatch'),
    field: 'reviewerBatch',
    flex: 112 / baseWidthOfDataGrid,
    minWidth: 112,
    isVisible: true,
  },
  {
    headerName: t(docStringPath + 'status'),
    headerClassName: 'last-column',
    field: 'status',
    flex: 123 / baseWidthOfDataGrid,
    minWidth: 123,
    isVisible: true,
  },
];

export const assignmentColumns: ColumnConfig[] = [
  {
    field: 'assignment',
    headerName: t(assignmentStringPath + 'assignment'),
    flex: 176 / baseWidthOfDataGrid,
    minWidth: 176,
    isVisible: true,
    cellClassName: 'first-column-cell',
    headerClassName: 'first-column-header',
  },
  {
    field: 'assignmentPath',
    headerName: t(assignmentStringPath + 'assignmentPath'),
    flex: 176 / baseWidthOfDataGrid,
    minWidth: 176,
    isVisible: true,
  },
  {
    field: 'reviewerBatch',
    headerName: t(assignmentStringPath + 'reviewerBatch'),
    flex: 176 / baseWidthOfDataGrid,
    isVisible: true,
    minWidth: 176,
  },
  {
    headerName: t(assignmentStringPath + 'InSet'),
    field: 'InSet',
    flex: 152 / baseWidthOfDataGrid,
    isVisible: true,
    cellClassName: 'in-set-cell',
    minWidth: 152,
  },
  {
    headerName: t(assignmentStringPath + 'InSetCompleted'),
    field: 'InSetCompleted',
    flex: 176 / baseWidthOfDataGrid,
    isVisible: true,
    cellClassName: 'in-set-completed-cell',
    minWidth: 176,
  },
  {
    headerName: t(assignmentStringPath + 'InSetLeftToDo'),
    headerClassName: 'last-column',
    field: 'InSetLeftToDo',
    flex: 176 / baseWidthOfDataGrid,
    isVisible: true,
    cellClassName: 'in-set-left-to-do-cell',
    minWidth: 176,
  },
];

export const getActionMenuOptions = (
  t: TFunction<['translation', ...string[]], undefined>,
  batchLevel: BatchLevel,
  selectedAssignmentIds: Map<string, string[]>,
  selectedDocs: Map<string, ABMDocResultItemType>
): CustomMenuItem[] => {
  let isUnassignDisabled = true;
  let isRedistributeDisabled = true;

  if (batchLevel === 'Assignment' && selectedAssignmentIds.size === 1) {
    const value = selectedAssignmentIds.values().next().value;
    isUnassignDisabled = !(
      value?.length === 1 && value?.includes('InSetLeftToDo')
    );
    isRedistributeDisabled = !(
      value?.length === 1 && value?.includes('InSetLeftToDo')
    );
  } else if (batchLevel === 'Document') {
    if (selectedDocs.size > 0) {
      isRedistributeDisabled = ![...selectedDocs.values()].every(
        (doc) => doc.status === 'Left to Do'
      );
    }
    isUnassignDisabled = [...selectedDocs.values()].some(
      (doc) => doc.status === 'COMPLETED'
    );
  }
  return [
    {
      id: ReportActionMenuOptionTypes.View,
      label: t('reports.actionMenu.view'),
      submenu: [
        {
          id: ReportActionMenuOptionTypes.ViewInDocView,
          label: t('reports.actionMenu.viewInDocView'),
        },
        {
          id: ReportActionMenuOptionTypes.ViewInDocViewNewTab,
          label: t('reports.actionMenu.viewInDocViewNewTab'),
        },
        {
          id: ReportActionMenuOptionTypes.ViewInDocList,
          label: t('reports.actionMenu.viewInDocList'),
        },
      ],
    },
    {
      id: ReportActionMenuOptionTypes.BulkTag,
      label: t('reports.actionMenu.bulkTag'),
    },
    {
      id: ReportActionMenuOptionTypes.BulkFolder,
      label: t('reports.actionMenu.bulkFolder'),
    },
    {
      id: ReportActionMenuOptionTypes.BulkAssign,
      label: t('reports.actionMenu.bulkAssign'),
    },
    batchLevel === 'Assignment'
      ? {
          id: ReportActionMenuOptionTypes.Tally,
          label: t('reports.actionMenu.tally'),
        }
      : null,
    {
      id: ReportActionMenuOptionTypes.ExportData,
      label: t('reports.actionMenu.exportData'),
    },
    {
      id: ReportActionMenuOptionTypes.Unassign,
      label: t('reports.actionMenu.unassign'),
      disabled: isUnassignDisabled,
    },
    {
      id: ReportActionMenuOptionTypes.Redistribute,
      label: t('reports.actionMenu.redistribute'),
      disabled: isRedistributeDisabled,
    },
  ].filter(Boolean) as CustomMenuItem[];
};

export const getDocumentListFilterOptions = (
  t: TFunction<['translation', ...string[]], undefined>
) => {
  return [
    {
      id: 'ALL',
      name: t('reports.advanceBatchManagement.documentFilterOption.all'),
    },
    {
      id: 'COMPLETED',
      name: t(
        'reports.advanceBatchManagement.documentFilterOption.onlyCompleted'
      ),
    },
    {
      id: 'NOTCOMPLETED',
      name: t(
        'reports.advanceBatchManagement.documentFilterOption.notCompleted'
      ),
    },
    {
      id: 'NOTDISTRIBUTED',
      name: t(
        'reports.advanceBatchManagement.documentFilterOption.notDistributed'
      ),
    },
  ];
};

export const getManageBatchTypedOptions = (
  t: TFunction<['translation', ...string[]], undefined>,
  selectedOption: string
) => {
  return [
    {
      id: 'Assignment',
      label: t('reports.advanceBatchManagement.assignmentType'),
      selected: selectedOption === 'Assignment',
    },
    {
      id: 'Document',
      label: t('reports.advanceBatchManagement.documentType'),
      selected: selectedOption === 'Document',
    },
  ];
};

export const convertServerBatchTypeToClient = (batchLevel?: string) => {
  if (batchLevel?.toUpperCase() === 'ASSIGNMENT') {
    return 'Assignment';
  } else {
    return 'Document';
  }
};

export const mapInSetCountKeyToGridField = (field: string) => {
  switch (field) {
    case 'InSet':
      return 'inSet';
    case 'InSetCompleted':
      return 'inSetCompleted';
    case 'InSetLeftToDo':
      return 'inSetLeftToDo';
  }
  return field;
};

export const getAppliedFilterValueObject = (
  apiData: ReviewResultsSources,
  savedReportData?: LeftPanelReportResponse
) => {
  const filterObject: {[key: string]: any} = {};

  if (!savedReportData) return filterObject;

  filterObject.selectedAssignments = (
    savedReportData?.selectedAssignments ?? []
  ).map((id) => id.toString());

  filterObject.selectedReviewers = apiData.reviewersList.map((reviewer) =>
    (savedReportData?.selectedReviewers ?? []).includes(reviewer.keyID)
  );

  const sourceMeta = getSectionMeta(
    savedReportData?.selectedSource.toLowerCase() ?? ''
  );

  filterObject[savedReportData?.selectedSource] = [
    {
      sourceName: sourceMeta.sourceName,
      navigationSourceType: sourceMeta.navigationSourceType,
      source: savedReportData.selectedSourceID.map((id) => id.toString()),
    },
  ];
  return filterObject;
};
